import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, CheckCheck } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setCount(r.data.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (open) api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  }, [open]);

  const openItem = async (n) => {
    try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    setOpen(false);
    loadCount();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* noop */ }
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setCount(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-secondary transition-colors" data-testid="notification-bell" title="Notificações">
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-semibold text-sm">Notificações</span>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="mark-all-read">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar tudo como lido
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="notifications-empty">Nenhuma notificação por aqui.</p>
          ) : items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary transition-colors ${n.read ? "" : "bg-primary/5"}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                <div className={n.read ? "opacity-70" : ""}>
                  <p className="text-sm leading-snug">{n.text}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{fmt(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
